import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Package,
  Calendar,
  RefreshCw,
  Users,
  Percent,
  Mail,
  Eye,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

import { useAuth } from "@/context/AuthContext";
import BusinessProfitability from "@/components/settings/analytics/BusinessProfitability";
import JobDuration from "@/components/settings/analytics/JobDuration";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable info tooltip for stat labels / section titles
function InfoTip({ text, testid }) {
  return (
    <TooltipProvider delayDuration={100}>
      <UITooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors cursor-help inline-flex align-middle"
            aria-label="More info"
            data-testid={testid}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px] text-xs">{text}</TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
}

// Component: Weekly Quote Performance Digest controls
function DigestActions() {
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const token = () => localStorage.getItem("auth_token");
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const loadPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await axios.get(`${API}/digest/quote-performance/preview`, { headers: headers() });
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
    } catch (e) {
      toast.error("Failed to load digest preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Send the Weekly Quote Performance Digest to info@alpinearmour.com.au and scott@scottlistondesign.com.au now?")) {
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/digest/quote-performance/send`, {}, { headers: headers() });
      toast.success(`Digest sent to ${(res.data.to || []).join(", ")}`, { duration: 6000 });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Failed to send digest";
      toast.error(detail, { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={loadPreview} className="h-9" data-testid="digest-preview-btn">
        <Eye className="w-3 h-3 mr-1" />
        Preview Digest
      </Button>
      <Button size="sm" onClick={sendNow} disabled={sending} className="h-9 bg-aa-primary" data-testid="digest-send-btn">
        <Mail className="w-3 h-3 mr-1" />
        {sending ? "Sending..." : "Send Digest Now"}
      </Button>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Digest Preview</DialogTitle>
            <DialogDescription>{previewSubject || "Loading…"}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto border rounded">
            {previewLoading ? (
              <div className="p-12 text-center text-gray-400">
                <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
                Building preview…
              </div>
            ) : (
              <iframe
                title="digest-preview"
                srcDoc={previewHtml}
                style={{ width: "100%", height: "60vh", border: "0" }}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button onClick={() => { setPreviewOpen(false); sendNow(); }} disabled={sending} className="bg-aa-primary">
              <Mail className="w-4 h-4 mr-2" />
              Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Time period presets matching Analytics.jsx
const TIME_PERIODS = {
  this_week: { label: "This Week", getRange: () => getThisWeekRange() },
  last_week: { label: "Last Week", getRange: () => getLastWeekRange() },
  this_month: { label: "This Month", getRange: () => getThisMonthRange() },
  last_month: { label: "Last Month", getRange: () => getLastMonthRange() },
  this_quarter: { label: "This Quarter", getRange: () => getThisQuarterRange() },
};

function formatDateStr(date) {
  return date.toISOString().split('T')[0];
}

function getThisWeekRange() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - now.getDay() + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: formatDateStr(monday), end: formatDateStr(sunday) };
}

function getLastWeekRange() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - now.getDay() - 6);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: formatDateStr(monday), end: formatDateStr(sunday) };
}

function getThisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: formatDateStr(start), end: formatDateStr(end) };
}

function getLastMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { start: formatDateStr(start), end: formatDateStr(end) };
}

function getThisQuarterRange() {
  const now = new Date();
  const month = now.getMonth();
  let qStart, qEnd;
  
  if (month >= 6 && month <= 8) {
    qStart = new Date(now.getFullYear(), 6, 1);
    qEnd = new Date(now.getFullYear(), 8, 30);
  } else if (month >= 9 && month <= 11) {
    qStart = new Date(now.getFullYear(), 9, 1);
    qEnd = new Date(now.getFullYear(), 11, 31);
  } else if (month >= 0 && month <= 2) {
    qStart = new Date(now.getFullYear(), 0, 1);
    qEnd = new Date(now.getFullYear(), 2, 31);
  } else {
    qStart = new Date(now.getFullYear(), 3, 1);
    qEnd = new Date(now.getFullYear(), 5, 30);
  }
  
  return { start: formatDateStr(qStart), end: formatDateStr(qEnd) };
}

const ANALYTICS_VIEWS = [
  { id: "overview", label: "Overview" },
  { id: "business-profitability", label: "Business Profitability", adminOnly: true },
  { id: "job-duration", label: "Job Duration", adminOnly: true },
];

export default function AnalyticsContent() {
  const { isAdmin } = useAuth();
  const [view, setView] = useState("overview");
  const views = ANALYTICS_VIEWS.filter((v) => !v.adminOnly || isAdmin);
  return (
    <div>
      <div className="flex gap-1 border-b border-gray-200 px-6 pt-4" data-testid="analytics-view-tabs">
        {views.map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === v.id ? "border-emerald-600 text-emerald-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid={`analytics-view-${v.id}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      {view === "business-profitability" && isAdmin ? (
        <BusinessProfitability />
      ) : view === "job-duration" && isAdmin ? (
        <JobDuration />
      ) : (
        <AnalyticsOverview />
      )}
    </div>
  );
}

function AnalyticsOverview() {
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("this_month");
  const [isLoading, setIsLoading] = useState(true);

  const getToken = () => localStorage.getItem("auth_token");

  const dateRange = useMemo(() => {
    return TIME_PERIODS[period]?.getRange() || getThisMonthRange();
  }, [period]);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    if (!dateRange.start || !dateRange.end) return;
    
    setIsLoading(true);
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = { start_date: dateRange.start, end_date: dateRange.end };
      const res = await axios.get(`${API}/analytics/summary`, { params, headers });
      setSummary(res.data);
    } catch (error) {
      console.error("Failed to load analytics", error);
      toast.error("Failed to load analytics");
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Analytics
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Business insights and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DigestActions />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TIME_PERIODS).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadAnalytics} className="h-9">
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
          Loading analytics...
        </div>
      ) : summary ? (
        (() => {
          // Map server response (which has nested .summary) to flat shape used below
          const s = summary.summary || {};
          const topCustomers = summary.top_customers || [];
          const byDecoration = summary.by_decoration || [];
          return (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-600">Total Jobs</span>
                <InfoTip testid="stat-info-total-jobs" text="Number of jobs created in the selected period." />
              </div>
              <p className="text-2xl font-bold text-blue-900">{s.total_jobs || 0}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-green-600">Revenue</span>
                <InfoTip testid="stat-info-revenue" text="Total quoted sales value for jobs in this period." />
              </div>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(s.total_revenue)}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 mb-1">
                <Percent className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-600">Avg Margin</span>
                <InfoTip testid="stat-info-avg-margin" text="Average profit margin across jobs in this period." />
              </div>
              <p className="text-2xl font-bold text-purple-900">{(s.avg_margin_percent || 0).toFixed(1)}%</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-medium text-amber-600">Avg Job Value</span>
                <InfoTip testid="stat-info-avg-job-value" text="Average revenue per job (total revenue ÷ number of jobs)." />
              </div>
              <p className="text-2xl font-bold text-amber-900">{formatCurrency(s.avg_order_value)}</p>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Unique Customers</span>
                <InfoTip testid="stat-info-unique-customers" text="Number of distinct customers quoted in this period." />
              </div>
              <p className="text-xl font-bold text-gray-900">{s.unique_customers || 0}</p>
            </div>
            <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Total Garments</span>
                <InfoTip testid="stat-info-total-garments" text="Total garment units across all jobs in this period." />
              </div>
              <p className="text-xl font-bold text-gray-900">{s.total_garments || 0}</p>
            </div>
            <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Total Cost</span>
                <InfoTip testid="stat-info-total-cost" text="Total production cost (garments, decoration and fees) for these jobs." />
              </div>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(s.total_cost)}</p>
            </div>
          </div>

          {/* Top Customers */}
          {topCustomers.length > 0 && (
            <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">Top Customers<InfoTip testid="stat-info-top-customers" text="Your highest-revenue customers in this period, with job count and revenue." /></h3>
              <div className="space-y-2">
                {topCustomers.slice(0, 5).map((customer, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded">
                    <span className="text-sm">{customer.name || "Unknown"}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {customer.job_count || 0} jobs
                      </Badge>
                      <span className="text-sm font-mono text-green-600">
                        {formatCurrency(customer.revenue || 0)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Decoration Methods Breakdown */}
          {byDecoration.length > 0 && (
            <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">By Decoration Method<InfoTip testid="stat-info-by-decoration" text="Revenue and job count grouped by the decoration / print method used." /></h3>
              <div className="space-y-2">
                {byDecoration.map((m, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded">
                    <span className="text-sm">{m.method || "Unknown"}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {m.count || 0} uses
                      </Badge>
                      <span className="text-sm font-mono">
                        {formatCurrency(m.revenue || 0)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
          );
        })()
      ) : (
        <div className="text-center py-12 text-gray-400">
          <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No analytics data available</p>
          <p className="text-xs mt-1">Complete some jobs to see analytics</p>
        </div>
      )}
    </div>
  );
}
